import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { getBillingHistory, resendReceipt } from "../lib/api";
import { getUser } from "../lib/auth";

const BLUE   = "#0176CE";
const ORANGE = "#F37428";

function fmt$(n) {
  if (n == null) return "—";
  return "$" + Number(n).toFixed(2);
}

function fmtDate(raw) {
  if (!raw) return "—";
  return new Date(raw).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function InvoiceModal({ payment, onClose }) {
  const user = getUser();
  const invoiceNum = "INV-" + String(payment.job_id).padStart(6, "0");
  const invoiceDate = fmtDate(payment.paid_on_utc);
  const rate = payment.record_count > 0 ? (Number(payment.amount_usd) / payment.record_count).toFixed(4) : "0.0000";

  function handlePrint() { window.print(); }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
        {/* Print/close controls — hidden when printing */}
        <div style={styles.modalControls} className="no-print">
          <button onClick={handlePrint} style={styles.printBtn}>Print / Save PDF</button>
          <button onClick={onClose} style={styles.closeBtn}>✕ Close</button>
        </div>

        {/* Invoice */}
        <div style={styles.invoice}>
          {/* Header */}
          <div style={styles.invHeader}>
            <div>
              <div style={styles.invBrand}>MasterFile<span style={{ color: ORANGE }}>AI</span></div>
              <div style={styles.invFromAddr}>
                MasterFile AI<br />
                www.masterfileai.com<br />
                DoNotReply@masterfileai.com
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={styles.invTitle}>INVOICE</div>
              <div style={styles.invMeta}><strong>Invoice #</strong> {invoiceNum}</div>
              <div style={styles.invMeta}><strong>Date</strong> {invoiceDate}</div>
              <div style={{ ...styles.invMeta, marginTop: 8 }}>
                <span style={styles.invPaidBadge}>PAID</span>
              </div>
            </div>
          </div>

          <div style={styles.invDivider} />

          {/* Bill To */}
          <div style={styles.invSection}>
            <div style={styles.invSectionLabel}>Bill To</div>
            <div style={styles.invBillTo}>
              {user?.full_name || "—"}<br />
              {user?.email || "—"}
            </div>
          </div>

          <div style={styles.invDivider} />

          {/* Line items */}
          <table style={styles.invTable}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={styles.invTh}>Description</th>
                <th style={{ ...styles.invTh, textAlign: "right" }}>Qty</th>
                <th style={{ ...styles.invTh, textAlign: "right" }}>Unit Price</th>
                <th style={{ ...styles.invTh, textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.invTd}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>Vendor/Customer Data Enrichment</div>
                  <div style={{ color: "#6b7280", fontSize: 12 }}>File: {payment.file_name}</div>
                </td>
                <td style={{ ...styles.invTd, textAlign: "right" }}>{(payment.record_count || 0).toLocaleString()}</td>
                <td style={{ ...styles.invTd, textAlign: "right" }}>${rate}</td>
                <td style={{ ...styles.invTd, textAlign: "right", fontWeight: 600 }}>{fmt$(payment.amount_usd)}</td>
              </tr>
            </tbody>
          </table>

          {/* Totals */}
          <div style={styles.invTotals}>
            <div style={styles.invTotalRow}>
              <span>Subtotal</span><span>{fmt$(payment.amount_usd)}</span>
            </div>
            <div style={styles.invTotalRow}>
              <span>Tax</span><span>$0.00</span>
            </div>
            <div style={styles.invDivider} />
            <div style={{ ...styles.invTotalRow, fontWeight: 700, fontSize: 16, marginTop: 8 }}>
              <span>Total</span><span style={{ color: BLUE }}>{fmt$(payment.amount_usd)}</span>
            </div>
          </div>

          {/* Footer */}
          <div style={styles.invFooter}>
            Thank you for your business. Questions? Contact us at DoNotReply@masterfileai.com
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Billing() {
  const [payments, setPayments]     = useState([]);
  const [loading,  setLoading]      = useState(true);
  const [error,    setError]        = useState("");
  const [sending,  setSending]      = useState({}); // { [jobId]: true }
  const [sent,     setSent]         = useState({}); // { [jobId]: true }
  const [viewInvoice, setViewInvoice] = useState(null);

  useEffect(() => {
    getBillingHistory()
      .then(r => setPayments(r.payments || []))
      .catch(e => setError(e.message || "Could not load billing history."))
      .finally(() => setLoading(false));
  }, []);

  async function handleResend(jobId) {
    setSending(s => ({ ...s, [jobId]: true }));
    try {
      await resendReceipt(jobId);
      setSent(s => ({ ...s, [jobId]: true }));
      setTimeout(() => setSent(s => ({ ...s, [jobId]: false })), 3000);
    } catch (e) {
      alert(e.message || "Could not send receipt.");
    } finally {
      setSending(s => ({ ...s, [jobId]: false }));
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.topStripe} />
      <div style={styles.body}>
      <Sidebar active="billing" />
      <main style={styles.main}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Billing</h1>
            <p style={styles.sub}>Your payment history and receipts</p>
          </div>
        </div>

        {loading && <div style={styles.empty}>Loading…</div>}
        {error   && <div style={{ color: "#ef4444", padding: "12px 0" }}>{error}</div>}

        {!loading && !error && payments.length === 0 && (
          <div style={styles.emptyBox}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>💳</div>
            <div style={{ color: "#111827", fontWeight: 600, marginBottom: 6 }}>No payments yet</div>
            <div style={{ color: "#6b7280", fontSize: 14 }}>Payments will appear here once you process a file.</div>
            <Link to="/upload" style={{ ...styles.btn, marginTop: 20, display: "inline-block" }}>Upload a file →</Link>
          </div>
        )}

        {!loading && payments.length > 0 && (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {["Date", "Invoice", "File", "Records", "Amount", "Actions"].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => (
                  <tr key={p.job_id} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                    <td style={styles.td}>{fmtDate(p.paid_on_utc)}</td>
                    <td style={{ ...styles.td, color: "#6b7280", fontFamily: "monospace", fontSize: 12 }}>
                      INV-{String(p.job_id).padStart(6, "0")}
                    </td>
                    <td style={styles.td}>
                      <Link to={`/results/${p.job_id}`} style={{ color: BLUE, textDecoration: "none", fontSize: 13 }}>
                        {p.file_name}
                      </Link>
                    </td>
                    <td style={styles.td}>{(p.record_count || 0).toLocaleString()}</td>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{fmt$(p.amount_usd)}</td>
                    <td style={styles.td}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          style={styles.actionBtn}
                          onClick={() => setViewInvoice(p)}
                        >
                          View invoice
                        </button>
                        <button
                          style={{ ...styles.actionBtn, opacity: sending[p.job_id] ? 0.6 : 1 }}
                          disabled={sending[p.job_id]}
                          onClick={() => handleResend(p.job_id)}
                        >
                          {sending[p.job_id] ? "Sending…" : sent[p.job_id] ? "✓ Sent!" : "Email receipt"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {viewInvoice && <InvoiceModal payment={viewInvoice} onClose={() => setViewInvoice(null)} />}
      </main>
      </div>
    </div>
  );
}

const styles = {
  page:         { display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f8fafc" },
  topStripe:    { height: 4, background: `linear-gradient(90deg, ${BLUE}, ${ORANGE})`, flexShrink: 0 },
  body:         { display: "flex", flex: 1 },
  main:         { flex: 1, padding: "40px 48px" },
  header:       { marginBottom: 32 },
  title:        { fontSize: 28, fontWeight: 700, color: "#111827", margin: 0 },
  sub:          { color: "#6b7280", fontSize: 14, margin: "4px 0 0" },
  empty:        { color: "#9ca3af", padding: "40px 0", textAlign: "center" },
  emptyBox:     { textAlign: "center", padding: "60px 40px", background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0" },
  tableWrap:    { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden" },
  table:        { width: "100%", borderCollapse: "collapse" },
  th:           { padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" },
  td:           { padding: "14px 16px", fontSize: 13, color: "#374151", borderBottom: "1px solid #f1f5f9" },
  btn:          { background: BLUE, color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontWeight: 600, fontSize: 13, cursor: "pointer", textDecoration: "none" },
  actionBtn:    { background: "transparent", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 12px", fontSize: 12, color: "#374151", cursor: "pointer", fontWeight: 500, whiteSpace: "nowrap" },

  // Modal
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, padding: "32px 16px", overflowY: "auto" },
  modalBox:     { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 680, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" },
  modalControls:{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "16px 20px", borderBottom: "1px solid #e2e8f0" },
  printBtn:     { background: BLUE, color: "#fff", border: "none", borderRadius: 7, padding: "8px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer" },
  closeBtn:     { background: "transparent", border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 14px", fontSize: 13, color: "#6b7280", cursor: "pointer" },

  // Invoice
  invoice:      { padding: "40px 48px 48px" },
  invHeader:    { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 },
  invBrand:     { fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.5px", marginBottom: 8 },
  invFromAddr:  { fontSize: 12, color: "#6b7280", lineHeight: 1.7 },
  invTitle:     { fontSize: 28, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.5px", marginBottom: 10 },
  invMeta:      { fontSize: 13, color: "#374151", marginBottom: 2 },
  invPaidBadge: { background: "#dcfce7", color: "#16a34a", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, letterSpacing: "0.06em" },
  invDivider:   { borderTop: "1px solid #e2e8f0", margin: "20px 0" },
  invSection:   { marginBottom: 20 },
  invSectionLabel: { fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 },
  invBillTo:    { fontSize: 14, color: "#111827", lineHeight: 1.7 },
  invTable:     { width: "100%", borderCollapse: "collapse", marginBottom: 0 },
  invTh:        { padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "2px solid #e2e8f0", textAlign: "left" },
  invTd:        { padding: "14px 12px", fontSize: 13, color: "#374151", borderBottom: "1px solid #f1f5f9" },
  invTotals:    { maxWidth: 280, marginLeft: "auto", marginTop: 16 },
  invTotalRow:  { display: "flex", justifyContent: "space-between", fontSize: 13, color: "#374151", marginBottom: 6 },
  invFooter:    { marginTop: 32, fontSize: 12, color: "#9ca3af", textAlign: "center", borderTop: "1px solid #f1f5f9", paddingTop: 20 },
};
